import React, { useState, useCallback, useEffect } from 'react'
import ReactDOM from 'react-dom'
import {
  Button,
  MaxHeightContainer,
  Spacer,
  Checkbox,
  FlexBox,
  Modal,
  Text,
  Table,
} from '@eltoro-ui/components'
import { useLocations } from 'Hooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faToggleOff, faToggleOn } from '@fortawesome/pro-regular-svg-icons'

import {
  getMapListings,
  getFilters,
  postFYP,
  getRealtorActivity,
} from 'Requests'
import {
  getDecryptedData,
  getIconBuzzColor,
  removeKeyFromObject,
} from 'Helpers'
import {
  APIFeatureType,
  FeatureFieldType,
  FilterDataType,
  FilterType,
  MinMaxType,
  TRealtorType,
  TRootState,
} from 'types'
import {
  BuzzScoreFilters,
  Map,
  MapPopup,
  MapSearch,
  MapToolTip,
  Realtor,
  BuzzScore,
} from 'Components'
import { browserLog } from 'Tools'
import toast from 'react-hot-toast'
import {
  AudienceResultsCard,
  AudienceResultsCount,
  BedroomNBathFilter,
  HomeTypeFilter,
  LocationLabels,
  MapMarker,
  PriceFilter,
  SaveAudienceWizard,
} from 'Pages/ProspectActivity/components'
import { LinearLoader } from 'Components/LinearLoader'
import { buzzFilters } from 'Pages/ProspectActivity/configs'
import { faWalking } from '@fortawesome/free-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import { SquareFootFilter } from 'Pages/ProspectActivity/components/FindProspectFilters/SquareFootFilter'
import { setSidebarLens } from 'Redux/actions'
import { postAreaSearch } from 'Requests/Request_Methods/prospectMethods'
import { ProspectsFilter } from '../../components/FindYourProspectsComponents'
import {
  LensFilterContainer,
  LensSectionHeading,
  LensSidebar,
  LensSidebarFooter,
  LensSidebarHeader,
} from '../components'
import './BeatTheMarket.scss'
import buzzScoreImg from '../../../../assets/Images/buzz_score.png'

const defaults = {
  listing_price: {
    id: 'listing_price',
    label: 'Listing price',
    options: { min: 0, max: 5000000 },
  },
  beds: {
    id: 'beds',
    label: 'Minimum number of bedrooms',
    options: [1, 2, 3, 4, 5],
  },
  baths: {
    id: 'baths',
    label: 'Minimum number of bathrooms',
    options: [1, 2, 3, 4, 5],
  },
  status: {
    id: 'status',
    label: 'Availibility Status',
    options: ['Active', 'Contingent', 'Under Contract', 'Sold'],
  },
  year_built: {
    id: 'year_built',
    label: 'Year Built',
    options: { min: 0, max: 2021 },
  },
}

export const BeatTheMarket = () => {
  const {
    locations,
    addLocation,
    removeLocation,
    hasLocations,
    clearLocation,
  } = useLocations()
  const [features, setFeatures] = useState<APIFeatureType[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<{ [key: string]: FilterType }>({})
  const dispatch = useDispatch()
  const [activeFilters, setActiveFilters] =
    useState<{ [key: string]: FilterType }>(defaults)
  const [activeBuzzFilters, setActiveBuzzFilters] = useState(buzzFilters)
  const [activeToggleFeature, setActiveToggleFeature] =
    useState<string>('buzz_filter')
  // Filters dropdown positioning
  const [searchRect, setSearchRect] = useState<DOMRect>()
  const measuredFiltersRef = useCallback(
    (node: HTMLButtonElement) => {
      if (node !== null) {
        setSearchRect(node.getBoundingClientRect())
      }
    },
    [showFilters],
  )
  // prospectFilters={beds:}
  // used for onChange in active Filter component - this needs to be debounced for server side filtering
  const updateActiveFilters = (filter: { [key: string]: FilterType }) => {
    setActiveFilters((prev) => ({
      ...prev,
      ...filter,
    }))
  }

  const [realtorFilters, setRealtorFilters] = useState({
    page: 1,
    sort: 'active_listings',
    order: 'desc',
  })
  const [realtorData, setRealtorData] = useState([])

  useEffect(() => {
    if (locations.length) {
      const bodySchema = {
        key: 'zip',
        value: '',
        size: 5,
        ...realtorFilters,
      }
      const body: any = []

      locations.map(
        (location: { key: string; value: string; display_value: string }) => {
          return body.push({ ...bodySchema, value: location.value })
        },
      )

      getRealtorActivity({ search: body })
        .then((res: any) => {
          if (res?.detail) {
            toast.success(res?.detail?.msg)
          }
          setRealtorData(res?.data?.result)
        })
        .catch((err) => toast.error(err))
    }
  }, [locations, realtorFilters])

  const onPageChange = (page: number) => {
    setRealtorFilters({ ...realtorFilters, page })
  }
  const onSort = () => {
    const order = realtorFilters.order === 'desc' ? 'asc' : 'desc'
    setRealtorFilters({ ...realtorFilters, order, page: 1 })
  }

  // add or remove filter object
  const handleFilterActivation = (key: string, checked: boolean) => {
    if (checked) {
      setActiveFilters((prev) => ({
        ...prev,
        [key]: filters[key],
      }))
    } else {
      setActiveFilters((prev) => removeKeyFromObject(key, prev))
    }
  }

  const isActiveFilter = (k: string) =>
    Object.keys(activeFilters).some((filter: string) => filter === k)

  const filterSelections = (filterSet: { [key: string]: FilterType }) => {
    return Object.entries(filterSet).reduce((acc, filter) => {
      if (filter[1].data) {
        if ((filter[1].data as { label: string }).label) {
          return {
            ...acc,
            [filter[0]]: parseInt(
              (filter[1].data as { label: string }).label,
              10,
            ),
          }
        }
        return {
          ...acc,
          [filter[0]]: filter[1].data,
        }
      }
      return acc
    }, {})
  }

  const showProspectsModal = () => {
    const { bedNBath, price: p, squareFoot: sf, homeType: ht } = modalOpen
    const { bed, bath, price, squareFoot, homeType } = currentFilters
    if (
      locations.length >= 1 &&
      isClicked.isLoaded &&
      !bedNBath &&
      !p &&
      !sf &&
      !ht &&
      (bed || bath || price || squareFoot || homeType)
    ) {
      return true
    }
    return false
  }

  // this filtering will most likely be done on the server, but some of the filtering might be more
  // perfomant with the client-side filtering of returned meta data of smaller API responses.
  // TLDR; this filtering should probably be done server-side
  const filterFeatures = (
    feats: APIFeatureType[],
    selectedFilters: { [key: string]: FilterDataType },
  ) => {
    return Object.entries(selectedFilters).reduce(
      (filteredFeatures: APIFeatureType[], flt) => {
        return filteredFeatures.reduce(
          (matchingFeatures: APIFeatureType[], feature) => {
            const matchingData = Object.entries(feature).reduce(
              (acc: any[], dataEntry) => {
                if (flt[0] === dataEntry[0]) {
                  if (dataEntry[1] && typeof flt[1] === 'object') {
                    if (
                      (flt[1] as MinMaxType).max >= 0 &&
                      (flt[1] as MinMaxType).min >= 0
                    ) {
                      const range = flt[1] as MinMaxType
                      if (
                        dataEntry[1] >= range.min &&
                        dataEntry[1] <= range.max
                      )
                        return [...acc, dataEntry]
                    }
                  }
                  if (dataEntry[1] && typeof flt[1] === 'number') {
                    if (dataEntry[1] >= flt[1]) return [...acc, dataEntry]
                  }
                  if (dataEntry[1] && Array.isArray(flt[1])) {
                    if (
                      flt[1].some(
                        (value: string | number) => dataEntry[1] === value,
                      )
                    )
                      return [...acc, dataEntry]
                  }
                  // data currently does not have a boolean meta data
                  /* if (dataEntry[1] && typeof flt[1] === 'boolean') {
                   *   if (dataEntry[1] === flt[1]) acc.concat(dataEntry)
                   * } */
                  return acc
                }
                return acc
              },
              [],
            )
            if (matchingData.length) return [...matchingFeatures, feature]
            return matchingFeatures
          },
          [],
        )
      },
      feats,
    )
  }
  const [buzzExcluded, setbuzzExcluded] = useState<string[]>()
  const [selectedListings, setSelectedListings] = useState<any>(null)
  const [listingArea, setlistingArea] = useState<any[]>([])
  const [buttonClicked, setbuttonClicked] = useState(false)
  const [buttonDisabled, setbuttonDisabled] = useState(false)
  const [isSubmitAreas, setisSubmitAreas] = useState(false)
  const [dataObj, setdataObj] = useState<any>()
  const [toggle, setToggle] = useState(true)
  const [loading, setLoading] = useState(false)

  const resetStates = () => {
    clearLocation()
    setlistingArea([])
    setSelectedListings([])
  }

  useEffect(() => {
    getFilters().then((res: any) => setFilters(res?.data?.filters))
  }, [])
  const AllBuzz: any = []
  const totalProspects = listingArea.reduce(
    (count, currFeature) => count + currFeature.unique_visitor_count,
    0,
  )
  const [range, setRange] = useState({ price: ['0', '1'], area: [1, 2] })
  const [filteres, setFilteres] = useState<any>([])

  const bedFilter = () =>
    postedFilter.bed &&
    filteres.push({
      key: 'beds',
      value: [postedFilter.bed],
      type: 'greater',
    })
  const bathFilter = () =>
    postedFilter.bath &&
    filteres.push({
      key: 'baths',
      value: [postedFilter.bath],
      type: 'greater',
    })
  const priceFilter = () =>
    postedFilter.price &&
    filteres.push({
      key: 'listing_price',
      value: [range?.price[0], range?.price[1]],
      type: 'between',
    })
  const homeFilter = () =>
    currentFilters.homeType &&
    filteres.push({
      key: 'home_type',
      value: [currentFilters.homeType],
      type: 'exact',
    })

  const areaFilter = () =>
    postedFilter.squareFoot &&
    filteres.push({
      key: 'size',
      value: [range?.area[0], range?.area[1]],
      type: 'between',
    })
  const searchProspect = () => {
    setRange({
      price: postedFilter?.price?.replaceAll(' ', '').split('-') || range.price,
      area:
        postedFilter?.squareFoot?.replaceAll(' ', '').split('-') || range.area,
    })
    // const realPrice = price.split('-')

    // filteres.push({
    //   key: 'beds',
    //   value: [postedFilter.bed],
    //   type: 'greater',
    // })
    bedFilter()
    bathFilter()
    areaFilter()
    priceFilter()
    homeFilter()

    setdataObj({
      exclude: buzzExcluded,
      search: locations[0],
      filters: filteres,
    })
    if (
      currentFilters.bath ||
      currentFilters.bed ||
      currentFilters.price ||
      currentFilters.homeType ||
      currentFilters.squareFoot
    ) {
      setLoading(true)
      setbuttonDisabled(true)
      postFYP({
        exclude: buzzExcluded,
        // search: locations[0],
        filters: filteres,
      })
        .then(async (res: any) => {
          setbuttonDisabled(false)

          setFilteres([])
          const lisitingsData = []
          const listingCounts = { count: 0 }
          if (res?.data) {
            // lisitingsData.push(...res?.data?.listings)
            const decryptedData = await getDecryptedData(res?.data?.response)

            lisitingsData.push(...decryptedData.listings)

            if (res.data) {
              setIsClicked({ ...isClicked, isLoaded: true })
              setlistingArea(lisitingsData)
              setSelectedListings(listingCounts.count)
            }
            // }
          } else {
            setFilteres([])
            setlistingArea([])
          }
        })
        .catch((err: any) => {
          setLoading(false)
          setbuttonDisabled(false)
        })
        .finally(() => {
          setbuttonClicked(false)
          setisSubmitAreas(false)
          setLoading(false)
          // dispatch(setSidebarLens(!openSideBar))
          if (openSideBar) dispatch(setSidebarLens(false))
          if (!locations.length) dispatch(setSidebarLens(true))
        })
    }
  }
  useEffect(() => {
    if (locations.length < 1) {
      setCurrentFilters({
        bed: null,
        bath: null,
        price: null,
        squareFoot: null,
        homeType: null,
      })
      setPostedFilter({
        bed: null,
        bath: null,
        price: null,
        squareFoot: null,
        homeType: null,
      })
      setIsClicked({ ...isClicked, isCancel: false, isLoaded: false })
      setlistingArea([])
    }
  }, [locations])

  // useEffect(() => {
  //   activeBuzzFilters.forEach((item) => AllBuzz.push(item.label))
  //   const Buzz = ['High buzz', 'Warm buzz', 'Medium buzz', 'Low buzz']
  //   const data = Buzz.filter(function (v: any) {
  //     return !AllBuzz.includes(v)
  //   })
  //   setbuzzExcluded(data)
  //   const revData = data.reverse()

  // }, [activeBuzzFilters])

  useEffect(() => {
    activeBuzzFilters.forEach((item) => AllBuzz.push(item.label))
    const Buzz = ['High buzz', 'Warm buzz', 'Medium buzz', 'Low buzz']
    const data = Buzz.filter(function (v: any) {
      return !AllBuzz.includes(v)
    })
    setbuzzExcluded(data)
    const revData = data.reverse()
  }, [activeBuzzFilters])

  useEffect(() => {
    if (hasLocations) {
      getMapListings(locations).then((res) => {
        if (res.data) {
          setFeatures(res.data.listings)
        }
      })
    } else {
      setFeatures([])
      // setCurrentFilters([])
    }
  }, [hasLocations, locations])
  const filteredFeatures = Object.keys(filterSelections(activeFilters)).length
    ? filterFeatures(features, filterSelections(activeFilters))
    : features

  useEffect(() => {
    searchProspect()
  }, [buzzExcluded])

  const [modalOpen, setIsModalOpen] = useState({
    bedNBath: false,
    price: false,
    squareFoot: false,
    homeType: false,
  })
  const initialModal = {
    bedNBath: false,
    price: false,
    squareFoot: false,
    homeType: false,
  }
  const [home, setHome] = useState<any>(null)

  const [currentFilters, setCurrentFilters] = useState({
    bed: null,
    bath: null,
    price: null,
    squareFoot: null,
    homeType: null,
  })
  const [postedFilter, setPostedFilter] = useState<any>({
    bed: null,
    bath: null,
    price: null,
    squareFoot: null,
    homeType: null,
  })
  const [isClicked, setIsClicked] = useState({
    isCancel: false,
    isLoaded: false,
  })

  const { openSideBar } = useSelector(
    (state: TRootState) => state.centerLensReducer,
  )

  const toggleSidebar = () => {
    dispatch(setSidebarLens(!openSideBar))
  }

  const searchButtonIsDisabled = () => {
    const { bath, bed, price, homeType, squareFoot } = currentFilters
    return ((!bath || !bed) && !price && !homeType && !squareFoot) || loading
  }

  return (
    <div className="SellYourListingFirst">
      <LensSidebar>
        <MaxHeightContainer
          fullHeight
          header={
            <LensSidebarHeader
              heading="Beat The Market"
              subHeading={
                <span>
                  Engage with <b>potential buyers</b> seen at listings that are
                  similiar to yours and advertise your listing
                </span>
              }
            />
          }
          footer={<></>}
        >
          <div className="LensSidebarHeader__heading LensSidebarHeader__heading__sub">
            Search a Location
          </div>
          <div className="LensSidebarHeader__sub-heading LensSidebarHeader__sub-heading__sub">
            e.g. City, Zip Code
          </div>
          {modalOpen.bedNBath ? (
            <div className="SellYourListingFirst__sideFilter bedrooms">
              <BedroomNBathFilter
                currentFilters={currentFilters}
                setBed={setCurrentFilters}
                setBath={setCurrentFilters}
                modalOpen={modalOpen}
                setIsModalOpen={setIsModalOpen}
                postedFilters={postedFilter}
                setPostedFilter={setPostedFilter}
                setIsClicked={setIsClicked}
                isClicked={isClicked}
              />
            </div>
          ) : (
            ''
          )}
          {modalOpen.homeType ? (
            <div className="SellYourListingFirst__sideFilter home_type">
              <HomeTypeFilter
                currentFilters={currentFilters}
                setHome={setCurrentFilters}
                modalOpen={modalOpen}
                setIsModalOpen={setIsModalOpen}
                setIsClicked={setIsClicked}
                isClicked={isClicked}
              />
            </div>
          ) : (
            ''
          )}

          {modalOpen.price ? (
            <div className="SellYourListingFirst__sideFilter price">
              <PriceFilter
                currentFilters={currentFilters}
                setPrice={setCurrentFilters}
                modalOpen={modalOpen}
                setIsModalOpen={setIsModalOpen}
                postedFilters={postedFilter}
                setPostedFilter={setPostedFilter}
                setIsClicked={setIsClicked}
                isClicked={isClicked}
              />
            </div>
          ) : (
            ''
          )}
          {modalOpen.squareFoot ? (
            <div className="SellYourListingFirst__sideFilter sqft">
              <SquareFootFilter
                currentFilters={currentFilters}
                postedFilters={postedFilter}
                setPostedFilter={setPostedFilter}
                setSquareFeet={setCurrentFilters}
                modalOpen={modalOpen}
                setIsModalOpen={setIsModalOpen}
                setIsClicked={setIsClicked}
                isClicked={isClicked}
              />
            </div>
          ) : (
            ''
          )}
          <div className="SellYourListingFirst__sidebar-content">
            {locations.length < 1 ? <MapSearch onSelect={addLocation} /> : ''}
            {hasLocations && (
              <>
                {locations.length < 1 ? <Spacer height="2rem" /> : ''}
                <LocationLabels
                  loading={loading}
                  locations={locations}
                  onRemove={removeLocation}
                />
                {locations.length < 1 ? <Spacer height="2rem" /> : ''}
                <Text on="white" size="m" weight="bold">
                  Filters:
                </Text>

                <div>
                  <Button
                    kind="default"
                    UNSAFE_className={`first_filter_button ${
                      currentFilters.bed && currentFilters.bath
                        ? 'SellYourListingFirst__sidebarFiltersButton'
                        : 'SellYourListingFirst__sidebarFiltersUnselectedButton'
                    }`}
                    size="l"
                    width="100%"
                    onClick={() => {
                      setIsModalOpen({ ...initialModal, bedNBath: true })
                    }}
                  >
                    {currentFilters.bed && currentFilters.bath
                      ? `${currentFilters.bed} Bed/${currentFilters.bath} Bath`
                      : 'Bed/Bath'}
                  </Button>
                  <Button
                    kind="default"
                    UNSAFE_className={
                      currentFilters.price
                        ? 'SellYourListingFirst__sidebarFiltersButton'
                        : 'SellYourListingFirst__sidebarFiltersUnselectedButton'
                    }
                    onClick={() => {
                      setIsModalOpen({ ...initialModal, price: true })
                    }}
                    size="l"
                    width="100%"
                  >
                    {currentFilters.price ? `${currentFilters.price}` : 'Price'}
                  </Button>
                  <Button
                    kind="default"
                    UNSAFE_className={
                      currentFilters.squareFoot
                        ? 'SellYourListingFirst__sidebarFiltersButton'
                        : 'SellYourListingFirst__sidebarFiltersUnselectedButton'
                    }
                    size="l"
                    width="100%"
                    onClick={() => {
                      setIsModalOpen({ ...initialModal, squareFoot: true })
                    }}
                  >
                    {currentFilters.squareFoot
                      ? `${currentFilters.squareFoot}`
                      : 'Squarefoot'}
                  </Button>
                  <Button
                    kind="default"
                    UNSAFE_className={
                      currentFilters.homeType
                        ? 'SellYourListingFirst__sidebarFiltersButton'
                        : 'SellYourListingFirst__sidebarFiltersUnselectedButton'
                    }
                    size="l"
                    width="100%"
                    onClick={() => {
                      setIsModalOpen({ ...initialModal, homeType: true })
                    }}
                  >
                    {currentFilters.homeType || 'Home Type'}
                  </Button>
                  <Button
                    UNSAFE_style={{
                      backgroundColor: '#007b94',
                      color: '#F8F8F8',
                      border: '2px solid #007B94',
                    }}
                    UNSAFE_className="MyListingsLens__FooterBtn"
                    kind="default"
                    size="l"
                    width="100%"
                    onClick={() => {
                      searchProspect()
                    }}
                    disabled={
                      searchButtonIsDisabled() ||
                      modalOpen.bedNBath ||
                      modalOpen.homeType ||
                      modalOpen.price ||
                      modalOpen.squareFoot
                    }
                  >
                    Search For Prospects
                  </Button>
                </div>
              </>
            )}
          </div>
        </MaxHeightContainer>
      </LensSidebar>

      {/* <div className="MyListingsLens__filter">
          <LensFilterContainer>
            <BuzzScoreFilters
              filters={buzzFilters}
              activeFilters={activeBuzzFilters}
              onChange={setActiveBuzzFilters}
            />
          </LensFilterContainer>
        </div> */}
      {locations.length >= 1 && isClicked.isLoaded && (
        <div className={`${!openSideBar ? '' : 'MyListingsLens__hide'}`}>
          <div
            className="ExpandYourMarketing__rightTop"
            style={{ display: 'flex' }}
          >
            <Button
              UNSAFE_className="ExpandYourMarketing__rightTopButton"
              UNSAFE_style={{
                background:
                  activeToggleFeature === 'buzz_filter' ? '#FFB708' : '',
                color: activeToggleFeature === 'buzz_filter' ? 'white' : '',
              }}
              onClick={() => {
                setActiveToggleFeature('buzz_filter')
              }}
            >
              Buzz Filter
            </Button>
            <Button
              UNSAFE_className="ExpandYourMarketing__rightTopButton"
              UNSAFE_style={{
                background:
                  activeToggleFeature === 'realtor_filter' ? '#FFB708' : '',
                color: activeToggleFeature === 'realtor_filter' ? 'white' : '',
              }}
              onClick={() => {
                setActiveToggleFeature('realtor_filter')
              }}
            >
              Realtor
            </Button>
          </div>
          <div className="MyListingsLens__filter">
            {activeToggleFeature === 'realtor_filter' ? (
              <div className="realtor_container">
                {realtorData.map((realtor: TRealtorType, index) => (
                  <Realtor
                    key={index}
                    subTitle="Top brokerages"
                    title={realtor.value}
                    onPageChange={onPageChange}
                    currentPage={realtor.current_page}
                    totalPages={realtor.total_pages}
                    isDataFound={!!realtor?.data?.length}
                  >
                    {!realtor?.data.length ? (
                      <div style={{ textAlign: 'center' }}>
                        <Text on="grey-050" kind="subdued">
                          No Data Found of {realtor.value}
                        </Text>
                      </div>
                    ) : (
                      <Table
                        rows={realtor?.data}
                        columns={[
                          {
                            path: 'rank',
                            label: 'RANK',
                            alignment: 'center',
                            RowCell: (listing) => (
                              <b className="table_cell_content">{index + 1}</b>
                            ),
                          },
                          {
                            path: 'mls_id',
                            label: 'BROKERAGE',
                            alignment: 'left',
                            RowCell: (listing) => <b>{listing.mls_id}</b>,
                          },
                          {
                            path: 'active_listings',
                            label: 'ACTIVE LISTINGS',
                            alignment: 'center',
                            onSort,
                          },
                        ]}
                      />
                    )}
                  </Realtor>
                ))}
              </div>
            ) : (
              <LensFilterContainer>
                <BuzzScoreFilters
                  filters={buzzFilters}
                  activeFilters={activeBuzzFilters}
                  onChange={setActiveBuzzFilters}
                />
                <div className="buzzScoreImg__container">
                  {/* <BuzzScore /> */}
                  {/* <img
                    src={buzzScoreImg}
                    className="buzzScoreImg__containerInside"
                    alt="..."
                  /> */}
                </div>
              </LensFilterContainer>
            )}
          </div>
        </div>
      )}

      {/* buzz score toggle */}
      <div
        style={{
          position: 'absolute',
          right: '50px',
          zIndex: 999,
          bottom: '44px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <p style={{ fontSize: '10px', marginRight: '5px' }}>Show Filters</p>
        <FontAwesomeIcon
          style={{ marginRight: '1.5rem', fontSize: '32px' }}
          size="1x"
          color={!openSideBar ? '#FFB708' : '#FF0000'}
          icon={!openSideBar ? faToggleOn : faToggleOff}
          onClick={toggleSidebar}
        />
      </div>
      {/* buzz score toggle */}

      {/* totalProspects &&  */}
      {showProspectsModal() ? (
        <div className="MyListingsLens__audienceCenter">
          <div className="MyListingsLens__border_radius">
            <LensSidebarFooter>
              <AudienceResultsCard>
                <div className="MyListingsLens__score">
                  <div className="MyListingsLens__scoreLeft">
                    <p
                      style={{
                        fontSize: '22px',
                        fontWeight: '600',
                        color: '#FFAB03',
                      }}
                    >
                      {totalProspects.toLocaleString()}
                    </p>
                  </div>
                  <div className="MyListingsLens__scoreRight">
                    {/* <b>Results Prospects</b> */}
                    <b style={{ fontSize: '22px', fontWeight: 'normal' }}>
                      Prospects observed
                    </b>
                    <br />
                    {/* have visited <b>{locations.length}</b> */}
                    {/* {locations.length} */}
                    {/* <b> listings</b> */}
                    {/* <b>{locations.length > 1 ? ' locations' : ' location'}</b> */}
                  </div>
                </div>
                <Spacer />
                <SaveAudienceWizard
                  // type="Sell Your Listing Faster"
                  type="Beat The Market"
                  dataObj={dataObj}
                  onSaveAndClose={(audienceDetails) => {
                    // browserLog.info(audienceDetails)
                    resetStates()
                  }}
                  totalProspects={totalProspects}
                  listing={filteredFeatures}
                />
              </AudienceResultsCard>
            </LensSidebarFooter>
          </div>
        </div>
      ) : (
        ''
      )}

      <div className="SellYourListingFirst__map">
        {/* {loading && <LinearLoader />} */}
        <Map
          features={listingArea}
          locations={[]}
          marker={(f) => <MapMarker color={getIconBuzzColor(f)} />}
          popup={(f) => <MapPopup feature={f} />}
          tooltip={(f) => <MapToolTip feature={f} />}
        />
      </div>
      {showFilters &&
        ReactDOM.createPortal(
          <div
            className="SellYourListingFirst__filter-selection"
            style={{
              top: `${searchRect?.bottom}px`,
              left: `${searchRect?.left}px`,
            }}
          >
            {Object.keys(filters).map((key: string) => (
              <Checkbox
                key={filters[key].label}
                checked={isActiveFilter(key)}
                onChange={(checked) => handleFilterActivation(key, checked)}
                label={filters[key].label}
              />
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
